import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Bell, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import BottomNav from "../components/BottomNav";
import { requestPushNotifications } from "../services/push";
import { isAppWebView } from "../utils/appWebView";

function NotificationsPage() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [items, setItems] = useState([]);
  const [enabling, setEnabling] = useState(false);
  const canEnableBrowserPush =
    !isAppWebView() &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission !== "granted";

  useEffect(() => {
    if (!user?.id) {
      navigate("/login", { replace: true });
      return;
    }

    let mounted = true;
    const load = async () => {
      try {
        const res = await API.get(`/notifications/${user.id}`);
        const nextItems = Array.isArray(res.data) ? res.data : [];
        if (mounted) setItems(nextItems);
        const latestId = nextItems.reduce((max, item) => Math.max(max, Number(item.id || 0)), 0);
        if (latestId) {
          localStorage.setItem(`mk_notifications_seen_${user.id}`, String(latestId));
        }
      } catch {
        if (mounted) setItems([]);
      }
    };

    load();
    const interval = window.setInterval(load, 30000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [navigate, user]);

  return (
    <div style={page}>
      <header style={header}>
        <button type="button" style={backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={22} />
        </button>
        <strong style={headerTitle}>Notifications</strong>
        <span style={headerSpacer} />
      </header>

      <main style={content}>
        <section style={heroCard}>
          <span style={heroIcon}><Bell size={22} /></span>
          <div>
            <strong style={heroTitle}>App Notifications</strong>
            <p style={heroSubtitle}>Admin messages and result updates will appear here automatically.</p>
            {canEnableBrowserPush && (
              <button
                type="button"
                style={enableButton}
                onClick={async () => {
                  if (!user?.id || enabling) return;
                  setEnabling(true);
                  try {
                    const enabled = await requestPushNotifications(user.id);
                    if (!enabled) {
                      window.alert("Browser notifications are still blocked. You can allow them from browser settings.");
                    }
                  } finally {
                    setEnabling(false);
                  }
                }}
              >
                {enabling ? "Enabling..." : "Enable Browser Alerts"}
              </button>
            )}
          </div>
        </section>

        <section style={list}>
          {items.map((item) => (
            <article key={item.id} style={itemCard}>
              <span style={itemIcon}><Sparkles size={16} /></span>
              <div style={itemBody}>
                <strong style={itemTitle}>{item.title}</strong>
                <p style={itemMessage}>{item.message}</p>
                <small style={itemDate}>{formatDate(item.created_at)}</small>
              </div>
            </article>
          ))}
          {!items.length && (
            <div style={emptyCard}>
              <strong>No notifications yet</strong>
              <p style={emptyText}>New admin announcements and result alerts will show here.</p>
            </div>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Just now";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit"
  });
}

const page = { minHeight: "100vh", background: "#f8fafc", paddingBottom: "84px" };
const header = { height: "56px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "grid", gridTemplateColumns: "40px 1fr 40px", alignItems: "center", gap: "16px", padding: "0 16px", position: "sticky", top: 0, zIndex: 10 };
const backButton = { width: "36px", height: "36px", border: 0, borderRadius: "50%", background: "#f8fafc", display: "grid", placeItems: "center" };
const headerTitle = { justifySelf: "center", fontSize: "18px", fontWeight: 800, color: "#0f172a" };
const headerSpacer = { width: 36 };
const content = { maxWidth: 540, margin: "0 auto", padding: 16, display: "grid", gap: 16 };
const heroCard = { display: "grid", gridTemplateColumns: "54px 1fr", gap: 14, alignItems: "center", background: "linear-gradient(135deg,#f0fdf4,#ffffff)", border: "1px solid #bbf7d0", borderRadius: 22, padding: 16 };
const heroIcon = { width: 48, height: 48, borderRadius: 16, background: "#16a34a", color: "#fff", display: "grid", placeItems: "center" };
const heroTitle = { display: "block", fontSize: 17, fontWeight: 900, color: "#14532d" };
const heroSubtitle = { margin: "4px 0 0", color: "#64748b", fontSize: 13, lineHeight: 1.45 };
const enableButton = { marginTop: 10, height: 38, border: 0, borderRadius: 999, padding: "0 16px", background: "#15803d", color: "#fff", fontWeight: 800, cursor: "pointer" };
const list = { display: "grid", gap: 12 };
const itemCard = { display: "grid", gridTemplateColumns: "36px 1fr", gap: 12, alignItems: "start", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 20, padding: 16, boxShadow: "0 8px 20px rgba(15,23,42,0.04)" };
const itemIcon = { width: 30, height: 30, borderRadius: 999, background: "#fff7ed", color: "#ea580c", display: "grid", placeItems: "center", marginTop: 2 };
const itemBody = { display: "grid", gap: 6 };
const itemTitle = { fontSize: 15, fontWeight: 800, color: "#111827" };
const itemMessage = { margin: 0, color: "#475569", fontSize: 13, lineHeight: 1.5 };
const itemDate = { color: "#94a3b8", fontSize: 12, fontWeight: 700 };
const emptyCard = { background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 20, padding: 20, textAlign: "center", color: "#475569" };
const emptyText = { margin: "8px 0 0", fontSize: 13, color: "#94a3b8" };

export default NotificationsPage;
